/**
 * FileProcessingBadge Component
 *
 * Inline status badge for file processing status.
 * Shows visual indicators for pending, processing, failed, or ready status.
 */

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { getProcessingStatus, ProcessingStatus } from '@/services/filesApi';

interface FileProcessingBadgeProps {
  fileId: string;
  contentType: string | null;
  compact?: boolean;
  onStatusChange?: (status: ProcessingStatus) => void;
}

// File types that support processing
const PROCESSABLE_TYPES = new Set([
  'application/pdf',
  'image/png',
  'image/jpeg',
  'image/jpg',
  'image/gif',
  'image/webp',
  'image/bmp',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'application/vnd.oasis.opendocument.text',
  'text/plain',
  'text/markdown',
  'text/csv',
  'application/json',
]);

/**
 * Check if a file type supports processing
 */
export function isProcessableType(contentType: string | null): boolean {
  if (!contentType) return false;
  return PROCESSABLE_TYPES.has(contentType);
}

/**
 * FileProcessingBadge - Inline status badge
 */
export default function FileProcessingBadge({
  fileId,
  contentType,
  compact = true,
  onStatusChange,
}: FileProcessingBadgeProps) {
  const { t } = useTranslation('files');
  const [status, setStatus] = useState<ProcessingStatus | null>(null);
  const [initialLoading, setInitialLoading] = useState(true);

  // Don't show badge for non-processable types
  if (!isProcessableType(contentType)) {
    return null;
  }

  // Fetch status on mount
  useEffect(() => {
    let mounted = true;

    const fetchStatus = async () => {
      try {
        const result = await getProcessingStatus(fileId);
        if (mounted) {
          setStatus(result);
          onStatusChange?.(result);
        }
      } catch (err) {
        // No processing record yet - this is normal for new files
        if (mounted) {
          setStatus({
            file_id: fileId,
            status: 'pending',
            processing_level: null,
            has_preview: false,
            has_markdown: false,
            has_ocr: false,
            has_embeddings: false,
            has_ascii: false,
            has_sense: false,
            has_scout: false,
            has_page_previews: false,
            ascii_page_count: null,
            error_message: null,
          });
        }
      } finally {
        if (mounted) {
          setInitialLoading(false);
        }
      }
    };

    fetchStatus();

    // Poll for updates if processing
    let pollInterval: NodeJS.Timeout | null = null;
    if (status?.status === 'processing') {
      pollInterval = setInterval(fetchStatus, 3000);
    }

    return () => {
      mounted = false;
      if (pollInterval) clearInterval(pollInterval);
    };
  }, [fileId, status?.status]);

  // Don't show anything while loading initial status
  if (initialLoading) {
    return null;
  }

  // Compact inline badge
  if (compact) {
    // Not yet processed - display-only badge
    if (!status || status.status === 'pending') {
      return (
        <span
          className="inline-flex items-center gap-1 px-1.5 py-0.5 text-xs bg-yellow-100 text-yellow-800 rounded"
          title={t('processing.not_processed')}
        >
          <span className="text-xs">⏳</span>
          {t('processing.not_ready')}
        </span>
      );
    }

    // Processing in progress
    if (status.status === 'processing') {
      return (
        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-xs bg-blue-100 text-blue-800 rounded">
          <span className="animate-spin text-xs">⚙️</span>
          {t('processing.processing')}
        </span>
      );
    }

    // Failed
    if (status.status === 'failed') {
      return (
        <span
          className="inline-flex items-center gap-1 px-1.5 py-0.5 text-xs bg-red-100 text-red-800 rounded"
          title={status.error_message || t('processing.failed')}
        >
          <span className="text-xs">❌</span>
          {t('processing.failed')}
        </span>
      );
    }

    // Ready - show green tick for fully processed files
    if (status.processing_level === 'full' || status.processing_level === 'ai') {
      return (
        <span className="inline-flex items-center px-1 text-xs text-green-600" title={t('processing.fully_processed')}>
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
          </svg>
        </span>
      );
    }
    return null;
  }

  // Full badge (non-compact) - show more details
  return (
    <div className="inline-flex items-center gap-2">
      {status?.status === 'pending' && (
        <span className="inline-flex items-center gap-1.5 px-2 py-1 text-xs font-medium bg-yellow-100 text-yellow-800 rounded-md">
          <span>⏳</span>
          {t('processing.not_ready')}
        </span>
      )}

      {status?.status === 'processing' && (
        <span className="inline-flex items-center gap-1.5 px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-md">
          <svg className="animate-spin h-3 w-3" viewBox="0 0 24 24">
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
              fill="none"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
          {t('processing.processing')}
        </span>
      )}

      {status?.status === 'failed' && (
        <span
          className="inline-flex items-center gap-1.5 px-2 py-1 text-xs font-medium bg-red-100 text-red-800 rounded-md"
          title={status.error_message || t('processing.failed')}
        >
          <span>❌</span>
          {t('processing.failed')}
        </span>
      )}

      {status?.status === 'ready' && (
        <span className="inline-flex items-center gap-1.5 px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded-md">
          <span>✅</span>
          {t('processing.ready')}
          {status.has_embeddings && <span className="text-purple-600">+ AI</span>}
        </span>
      )}

    </div>
  );
}

/**
 * Simplified status indicator (no interactivity)
 */
export function FileStatusIndicator({
  status,
}: {
  status: 'pending' | 'processing' | 'ready' | 'failed';
}) {
  const { t } = useTranslation('files');
  switch (status) {
    case 'pending':
      return (
        <span className="inline-flex items-center gap-1 text-yellow-600" title={t('processing.not_processed')}>
          <span className="w-2 h-2 bg-yellow-500 rounded-full" />
        </span>
      );
    case 'processing':
      return (
        <span className="inline-flex items-center gap-1 text-blue-600" title={t('processing.processing')}>
          <span className="w-2 h-2 bg-blue-500 rounded-full animate-pulse" />
        </span>
      );
    case 'failed':
      return (
        <span className="inline-flex items-center gap-1 text-red-600" title={t('processing.failed_retry')}>
          <span className="w-2 h-2 bg-red-500 rounded-full" />
        </span>
      );
    case 'ready':
      return null; // No indicator for ready status
    default:
      return null;
  }
}
